import type {Ref, ComponentPropsWithoutRef, ComponentType} from 'react';
import React from 'react';

import {FloatingMenu, FloatingMenuBody} from '@pb/kit-component-menu';
import {withCssModule} from '@pb/kit-util-with-css-module';

import styles from './listbox.component.module.styl';

import type {FloatingMenuProps} from '@pb/kit-component-menu';


const classNames = withCssModule(styles);

type _FloatingMenuProps = Omit<FloatingMenuProps, 'onChange'>;

export interface ListboxProps extends _FloatingMenuProps {
    /**
     * Ref returned by `useListbox().getListBoxProps().floatingElRef`. Required
     * for Floating UI to measure and position the listbox correctly.
     */
    floatingElRef: Ref<HTMLDivElement>,
    /**
     * Replacement for the default `FloatingMenuBody` scroll container. Pass a
     * custom `div`-compatible component when you need a custom scroll behavior
     * or virtualization.
     */
    bodyComponent?: ComponentType<ComponentPropsWithoutRef<'div'>> | string,
}

/**
 * Floating listbox that positions `ListBoxItem` children next to an anchor
 * element. Use this when `DropdownMenu`, `ComboBox`, or `Autosuggest` do not
 * fit your requirements and you need to compose a listbox manually.
 *
 * Pair with `useListbox` for positioning, keyboard navigation, and virtual
 * focus management. The placement flips automatically between `bottom-*` and
 * `top-*` based on available viewport space.
 *
 * ## Accessibility
 * Implements the WAI-ARIA Combobox and Listbox patterns. The anchor element
 * must be an interactive element; its `role` defaults to `"combobox"`.
 *
 * @see [Storybook]{@link https://storybook.dev.pitchbookdata.com/?path=/docs/utils-listbox--documentation}
 */
export function Listbox(props: ListboxProps) {
    const {
        children,
        floatingElRef,
        bodyComponent: BodyComponent = FloatingMenuBody,
        ...floatingMenuProps
    } = props;

    const flip = floatingMenuProps.getContext().placement.startsWith('top-');

    return (
        <FloatingMenu
            {...floatingMenuProps}
            className={classNames(floatingMenuProps.className, {
                floating_mix: !flip,
                floating_mix_flipped: flip,
            })}
            floatingFocusManagerProps={{
                ...floatingMenuProps.floatingFocusManagerProps,
                modal: false,
                initialFocus: -1,
            }}
            flip={flip}
            ref={floatingElRef}>
            <BodyComponent>
                {children}
            </BodyComponent>
        </FloatingMenu>
    );
}
